// TODO: uncomment when functionality to upload plots in UI is fixed
import { Button, cn, Divider, Stack } from 'component-library';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { IndicatorName, PlotReportFactLabelNameEnum } from '@/api/rest/resources/types/fact';
import {
  ProtectedAreasOsm,
  ProtectedAreasOsmRealm,
  ProtectedAreasOsmRealmMap,
} from '@/api/rest/resources/types/protectedAreas';
import { NO_RADIUS, useMapRadiusContext } from '@/components/MapOverview/components/MapRadiusContextProvider';
import { NCCard, NCCard2SectionLayout, NCCardComplianceFooter, NCCardHeader } from '@/components/NCCard/NCCard';
import { ProtectedAreasExplainer } from '@/components/NCExplainers/ProtectedAreasExplainer';
import { COMPLIANCE_LOGOS_PROTECTED_AREAS } from '@/config/constants';
import { useProtectedAreasMethodology } from '@/hooks/useProtectedAreasMethodology';
import { usePlotFactsByRadius } from '@/pages/shared/hooks/usePlotFact';
import { getFactsFromFactArray } from '@/pages/shared/hooks/utils';

import { useLandscapeViewContext } from '../../LandscapeViewContextProvider';
import { PlotCardWrapper } from '../PlotCardWrapper';
import { ProtectedAreasGraphTile } from './GraphTile';
import { ProtectedAreasStatsTile } from './StatsTile';

export const ProtectedAreas = () => {
  const { t } = useTranslation();

  return (
    <PlotCardWrapper name={t('shared.ncaDetail.details.protectedAreas.labels.protectedAreas')}>
      <ProtectedAreasCard />
    </PlotCardWrapper>
  );
};

const ProtectedAreasCard = () => {
  const { isLandscapeActive } = useLandscapeViewContext();
  const { hasOsm } = useProtectedAreasMethodology();
  const { t } = useTranslation();
  const { radius } = useMapRadiusContext();
  const radiusLabel = radius && radius !== NO_RADIUS ? ` ${t('shared.projects.labels.radiusHint', { radius })}` : '';

  const [activeRealm, setActiveRealm] = useState<ProtectedAreasOsmRealm>(ProtectedAreasOsmRealm.terrestrial);

  const factNames = {
    graph: IndicatorName.protected_areas_area,
    latestAnalysis: IndicatorName.protected_areas_area,
    minDistance: IndicatorName.protected_areas_min_distance,
  };

  let facts = usePlotFactsByRadius(Object.values(factNames));

  if (hasOsm) {
    facts = facts?.filter((fact) => {
      const category = fact.labels?.find((label) => label.name === PlotReportFactLabelNameEnum.protection_category)
        ?.value as ProtectedAreasOsm;
      return ProtectedAreasOsmRealmMap[`${activeRealm}`].includes(category);
    });
  }

  const graphData = getFactsFromFactArray(facts, factNames.graph) ?? [];
  const statsData = [
    ...(getFactsFromFactArray(facts, factNames.latestAnalysis) ?? []),
    ...(getFactsFromFactArray(facts, factNames.minDistance) ?? []),
  ];

  if (!isLandscapeActive) {
    return null;
  }

  if (graphData.length === 0 && statsData.length === 0) {
    return null;
  }

  return (
    <NCCard explainer={<ProtectedAreasExplainer />}>
      <NCCardHeader
        title={t('shared.ncaDetail.details.protectedAreas.labels.protectedAreas') + radiusLabel}
        className='flex flex-row justify-between items-center gap-2'
      >
        {hasOsm && (
          <Stack className='flex flex-row gap-2 self-center px-2 w-fit'>
            {Object.keys(ProtectedAreasOsmRealm).map((realm) => (
              <Button
                size='small'
                key={realm}
                variant='outline'
                className={cn(
                  activeRealm === ProtectedAreasOsmRealm[realm as keyof typeof ProtectedAreasOsmRealm]
                    ? 'bg-primary-12 text-white'
                    : 'bg-white text-text-secondary',
                )}
                onClick={() => setActiveRealm(ProtectedAreasOsmRealm[realm as keyof typeof ProtectedAreasOsmRealm])}
              >
                {t(`global.protectedAreas.realms.${realm as keyof typeof ProtectedAreasOsmRealm}`)}
              </Button>
            ))}
          </Stack>
        )}
      </NCCardHeader>
      <NCCard2SectionLayout>
        <ProtectedAreasGraphTile protectedAreas={graphData} />
        <ProtectedAreasStatsTile protectedAreasSummary={statsData} />
      </NCCard2SectionLayout>
      <Divider />
      <NCCardComplianceFooter logos={COMPLIANCE_LOGOS_PROTECTED_AREAS} />
    </NCCard>
  );
};
